import * as A from 'astronomy-engine';
import { signal } from '@preact/signals';
import type { Place } from '../astro/frame';
import { idbGet, idbSet } from '../lib/idb';
import { clamp } from '../lib/math';
import { fetchWithin } from '../lib/net';
import { eyeLimit } from '../render/nakedeye';
import { CloudHour, loadClouds, num } from './conditions';

// Everything a stargazer needs from the weather, hour by hour: the chance of a clear sky (from three
// ensembles, not one model's guess), seeing, transparency, dew, fog and frost. All free and keyless:
// Open-Meteo (CC BY 4.0; Met Office data CC BY-SA 4.0), Copernicus CAMS aerosols and NOAA GFS profiles.

const HOUR = 3600e3;
const FRESH = 90 * 60e3;

// ---------- A small cache: memory, then IndexedDB, then the network ----------
const memo = new Map<string, { v: unknown; fetched: number }>();
async function cached<T>(key: string, get: () => Promise<T>): Promise<T | null> {
  const m = memo.get(key);
  if (m && Date.now() - m.fetched < FRESH) return m.v as T;
  const c = await idbGet<{ v: T; fetched: number }>(key);
  if (c && Date.now() - c.fetched < FRESH) {
    memo.set(key, c);
    return c.v;
  }
  try {
    const e = { v: await get(), fetched: Date.now() };
    memo.set(key, e);
    await idbSet(key, e);
    return e.v;
  } catch {
    return c?.v ?? null; // an older forecast beats none
  }
}

async function getJSON(url: string) {
  const j = await fetchWithin(url, (r) => {
    if (!r.ok) throw new Error(String(r.status));
    return r.json();
  });
  if (j.error) throw new Error(j.reason);
  return j;
}

const at = (p: Place) => `latitude=${p.lat.toFixed(2)}&longitude=${p.lon.toFixed(2)}`;

// ---------- Chance of a clear sky: 100 or more ensemble members from three centres ----------
// Each model's members are counted separately and the models then averaged, so ECMWF's 51 members
// do not drown out the Met Office's 18.
const inEurope = (lat: number, lon: number) => lat > 30 && lat < 70 && lon > -23 && lon < 45;
/** Whose ensembles give the clear-sky chance here, and how many runs in all. */
export const ensembleSources = (lat: number, lon: number) =>
  inEurope(lat, lon) ? { runs: 109, centres: 'ECMWF, DWD and Met Office' } : { runs: 100, centres: 'ECMWF, NOAA and Met Office' };
export const CLEAR_BELOW = 30; // % cloud that still counts as clear enough

export function clearChance(hourly: Record<string, (number | null)[]>): Map<number, number> {
  const byModel = new Map<string, string[]>();
  for (const k of Object.keys(hourly)) {
    if (k === 'time') continue;
    const model = k.replace(/^cloud_cover(_member\d+)?_/, '');
    byModel.set(model, [...(byModel.get(model) ?? []), k]);
  }
  const out = new Map<number, number>();
  (hourly.time as number[]).forEach((t, i) => {
    let sum = 0, n = 0;
    for (const keys of byModel.values()) {
      const vals = keys.map((k) => num(hourly[k][i], 0, 100)).filter((v): v is number => v != null);
      if (vals.length < 5) continue;
      sum += vals.filter((v) => v < CLEAR_BELOW).length / vals.length;
      n++;
    }
    if (n) out.set(t * 1000, sum / n);
  });
  return out;
}

async function loadEnsemble(p: Place) {
  const models = inEurope(p.lat, p.lon) ? 'ecmwf_ifs025,icon_eu_eps,ukmo_global_ensemble_20km' : 'ecmwf_ifs025,gfs025,ukmo_global_ensemble_20km';
  return cached(`ens:${p.lat.toFixed(2)},${p.lon.toFixed(2)}`, async () => {
    const j = await getJSON(`https://ensemble-api.open-meteo.com/v1/ensemble?${at(p)}&hourly=cloud_cover&models=${models}&forecast_days=3&timeformat=unixtime&timezone=GMT`);
    return [...clearChance(j.hourly)];
  });
}

// ---------- Transparency: aerosols (CAMS) plus air and ozone ----------
/** Rayleigh optical depth at wavelength λ (µm) for surface pressure p (hPa): Hansen & Travis (1974). */
const tauRayleigh = (um: number, p: number) => 0.008569 * um ** -4 * (1 + 0.0113 * um ** -2 + 0.00013 * um ** -4) * (p / 1013.25);
const TAU_OZONE = 0.03; // Chappuis band near 550 nm, about 300 Dobson units

/** Visual extinction in magnitudes per airmass from the aerosol optical depth at 550 nm. */
export const extinction = (aod: number, pressure = 1013.25) => 1.0857 * (tauRayleigh(0.55, pressure) + TAU_OZONE + aod);

async function loadAerosol(p: Place) {
  return cached(`aod:${p.lat.toFixed(2)},${p.lon.toFixed(2)}`, async () => {
    const j = await getJSON(`https://air-quality-api.open-meteo.com/v1/air-quality?${at(p)}&hourly=aerosol_optical_depth&forecast_days=3&timeformat=unixtime&timezone=GMT`);
    return (j.hourly.time as number[]).map((t, i) => [t * 1000, num(j.hourly.aerosol_optical_depth[i], 0, 10)] as const);
  });
}

// ---------- Seeing: the AXP model on GFS pressure levels ----------
// Trinquet & Vernin (2006, PASP 118:756), as fitted to GFS pressure levels by Ye (2011, PASP 123:113,
// Table 2): C_T² = ⟨C_T²⟩(h)·[A(h)·dθ/dz]^p(h) in 1 km layers, then Fried's r₀ and ε = 0.98 λ/r₀.
// The three boundary-layer rows share one p and A in the table; |dθ/dz| is floored to keep the power real.
const LEVELS = [1000, 975, 950, 925, 900, 850, 800, 750, 700, 650, 600, 550, 500, 450, 400, 350, 300, 250, 200, 150, 100, 70, 50, 30, 20, 10];
/** Free-atmosphere layers from 1 km above sea level, 1 km thick: [p, A, ⟨C_T²⟩]. */
const FREE: [number, number, number][] = [
  [0.3, 1.8e3, 5.2e-5], [1.3, 5.6e2, 4.2e-5], [1.7, 4.6e2, 2.8e-5], [1.7, 3.8e2, 2.5e-5], [2.6, 2.6e2, 1.8e-5],
  [1.1, 4.6e2, 1.5e-5], [0.8, 6.8e2, 1.6e-5], [0.6, 1.0e3, 1.6e-5], [0.3, 2.2e3, 1.9e-5], [0.5, 6.8e2, 2.6e-5],
  [0.7, 3.2e2, 3.4e-5], [0.6, 2.2e2, 4.4e-5], [0.1, 8.3e3, 4.8e-5], [0.2, 1.0e3, 5.5e-5], [-0.4, 3.2e1, 6.5e-5],
  [-0.3, 1.5e1, 8.3e-5], [2.5, 5.6e1, 1.1e-4], [-0.9, 2.6e1, 1.1e-4], [3.3, 3.8e1, 9.5e-5], [-1.0, 2.6e1, 8.2e-5],
  [1.5, 4.6e1, 7.4e-5], [-1.9, 3.2e1, 7.5e-5], [1.3, 4.6e1, 8.7e-5], [1.1, 3.8e1, 1.1e-4], [1.5, 3.8e1, 1.3e-4],
];
const LAMBDA = 5e-7;
// Calibrated against measured seeing (ESO Paranal MASS-DIMM and Maunakea DIMM/MASS, 2023–24; tested on
// La Silla, 2026 forecasts and published medians at nine sites): the boundary layer is about twice too
// strong on sites that stand above the model's smoothed terrain (Open-Meteo carries the grid cell's
// near-surface air up to them), and the free atmosphere has the right level but no night-to-night skill
// until shear at the jet stream is added. Medians then agree within about 2% at both fitted sites.
const BL_SUMMIT = 0.35; // boundary-layer factor once a site stands 300 m above the grid cell's terrain
const FA_SCALE = 0.5; // free-atmosphere factor, alongside the jet term
const JET_J = 0.6e-13; // m^(1/3) at a 30 m/s jet: shear turbulence grows as the square of its speed
const blFactor = (dz: number) => BL_SUMMIT ** clamp(dz / 300, 0, 1);
const theta = (T: number, P: number) => T * (1000 / P) ** 0.286;

type Pt = [z: number, T: number, P: number];

/**
 * Seeing in arcseconds at the zenith for one hour of a GFS profile (index i), for a site at `elev` metres
 * in a grid cell whose terrain is at `gridElev`.
 */
export function seeingAt(H: Record<string, (number | null)[]>, i: number, elev: number, ground = true, gridElev = elev): number | null {
  const ps = H.surface_pressure?.[i], t2 = H.temperature_2m?.[i];
  if (ps == null || t2 == null) return null;
  const pts: Pt[] = [[elev + 2, t2 + 273.15, ps]];
  for (const hh of [80, 120, 180]) {
    const t = H[`temperature_${hh}m`]?.[i];
    if (t != null) pts.push([elev + hh, t + 273.15, ps * Math.exp(-hh / 8400)]);
  }
  for (const l of LEVELS) {
    const t = H[`temperature_${l}hPa`]?.[i], z = H[`geopotential_height_${l}hPa`]?.[i];
    if (t == null || z == null || l > ps - 1 || (z <= pts[pts.length - 1][0] && z < elev + 200)) continue;
    pts.push([z, t + 273.15, l]);
  }
  pts.sort((a, b) => a[0] - b[0]);
  if (pts.length < 10) return null;
  let k = 0;
  const sample = (z: number) => {
    while (k < pts.length - 2 && pts[k + 1][0] < z) k++;
    const a = pts[k], b = pts[k + 1];
    if (z < a[0] || z > b[0] || b[0] <= a[0]) return null;
    const f = (z - a[0]) / (b[0] - a[0]);
    const T = a[1] + f * (b[1] - a[1]);
    const P = Math.exp(Math.log(a[2]) + f * (Math.log(b[2]) - Math.log(a[2])));
    const dth = (theta(b[1], b[2]) - theta(a[1], a[2])) / (b[0] - a[0]);
    return { T, P, g: Math.max(Math.abs(dth), 1e-4) };
  };
  const cn2 = (ct2: number, T: number, P: number) => ct2 * ((7.9e-5 * P) / (T * T)) ** 2;
  const STEP = 50;
  let Jbl = 0, Jfa = 0;
  if (ground) {
    for (let h = 25; h < 1000; h += STEP) {
      const s = sample(elev + h);
      if (!s) continue;
      const c = h < 50 ? 1.4e-2 : h < 100 ? 4.8e-4 * (h / 100) ** -2.6 : 3.4e-5 * (h / 1000) ** -1.1;
      Jbl += cn2(c * (160 * s.g) ** 0.5, s.T, s.P) * STEP;
    }
  }
  k = 0;
  FREE.forEach(([p, Acoef, c], n) => {
    for (let z = (n + 1) * 1000 + STEP / 2; z < (n + 2) * 1000; z += STEP) {
      if (z <= elev + 1000) continue;
      const s = sample(z);
      if (s) Jfa += cn2(c * (Acoef * s.g) ** p, s.T, s.P) * STEP;
    }
  });
  const jet = Math.max(0, ...[200, 250, 300].map((l) => H[`wind_speed_${l}hPa`]?.[i] ?? 0)); // m/s
  const J = blFactor(elev - gridElev) * Jbl + FA_SCALE * Jfa + JET_J * (jet / 30) ** 2;
  if (!(J > 0)) return null;
  const r0 = (0.423 * ((2 * Math.PI) / LAMBDA) ** 2 * J) ** (-3 / 5);
  return ((0.98 * LAMBDA) / r0) * 206264.806;
}

/** The GFS grid cell's own terrain height here (m), which its near-surface air belongs to; kept for good. */
async function gridElevation(p: Place): Promise<number | null> {
  const key = `gridelev:${p.lat.toFixed(2)},${p.lon.toFixed(2)}`;
  const c = await idbGet<number>(key);
  if (typeof c === 'number') return c;
  try {
    const j = await getJSON(`https://api.open-meteo.com/v1/forecast?${at(p)}&models=gfs_seamless&elevation=nan&hourly=surface_pressure&forecast_days=1`);
    if (typeof j.elevation !== 'number') return null;
    await idbSet(key, j.elevation);
    return j.elevation;
  } catch {
    return null;
  }
}

async function loadSeeing(p: Place) {
  return cached(`seeing:${p.lat.toFixed(2)},${p.lon.toFixed(2)}`, async () => {
    const vars = LEVELS.flatMap((l) => [`temperature_${l}hPa`, `geopotential_height_${l}hPa`]);
    vars.push('wind_speed_200hPa', 'wind_speed_250hPa', 'wind_speed_300hPa', 'temperature_2m', 'temperature_80m', 'temperature_120m', 'surface_pressure');
    const [j, grid] = await Promise.all([
      getJSON(`https://api.open-meteo.com/v1/forecast?${at(p)}&hourly=${vars.join(',')}&models=gfs_seamless&wind_speed_unit=ms&forecast_days=3&timeformat=unixtime&timezone=GMT`),
      gridElevation(p),
    ]);
    const H = j.hourly as Record<string, (number | null)[]>;
    const elev = j.elevation ?? 0;
    return (H.time as number[]).map((t, i) => {
      const jet = Math.max(...['200', '250', '300'].map((l) => H[`wind_speed_${l}hPa`]?.[i] ?? 0));
      return [t * 1000, seeingAt(H, i, elev, true, grid ?? elev), jet] as const;
    });
  });
}

// ---------- Putting it together ----------
export type Grade = 0 | 1 | 2 | 3 | 4; // excellent, good, average, poor, very poor
export const GRADE_WORDS = ['excellent', 'good', 'average', 'poor', 'very poor'] as const;
export const seeingGrade = (s: number): Grade => (s <= 1 ? 0 : s <= 1.5 ? 1 : s <= 2 ? 2 : s <= 2.5 ? 3 : 4);
export function transparencyGrade(kV: number, high: number | null, visKm: number | null): Grade {
  let g = kV < 0.25 ? 0 : kV < 0.33 ? 1 : kV < 0.45 ? 2 : kV < 0.6 ? 3 : 4;
  if ((high ?? 0) > 30) g++; // thin high cloud dims everything
  if (visKm != null && visKm < 10) g++; // mist or haze near the ground
  return Math.min(4, g) as Grade;
}

export type Risk = 0 | 1 | 2; // none, possible, likely

export interface SkyHour {
  t: number;
  cloud: number | null; // % (Met Office in the UK)
  high: number | null;
  ens: number | null; // share of ensemble members that are clear, 0–1
  pClear: number | null; // blend of the two, 0–1
  seeing: number | null; // arcseconds
  jet: number | null; // fastest wind at 9–12 km, m/s
  kV: number | null; // extinction, magnitudes per airmass
  transparency: Grade | null;
  dew: Risk;
  fog: Risk;
  frost: boolean;
  temp: number | null;
  feels: number | null;
  wind: number | null; // m/s
}

/** Weight given to the Met Office's single forecast against the ensembles, by how far ahead it is. */
const detWeight = (leadH: number) => (leadH < 6 ? 0.7 : leadH < 24 ? 0.5 : 0.35);
/** Cloud cover as a rough chance of clear sky: certain at 20% or less, none at 60% or more. */
const detClear = (cloud: number) => clamp((60 - cloud) / 40, 0, 1);

export function dewFog(h: CloudHour): { dew: Risk; fog: Risk; frost: boolean } {
  const spread = h.temp != null && h.dew != null ? h.temp - h.dew : null;
  const calm = (h.wind ?? 5) < 3, clearish = h.total < 50;
  let dew: Risk = 0, fog: Risk = 0;
  if (spread != null) {
    if (spread <= 2 && calm && clearish) dew = 2;
    else if (spread <= 4 && h.total < 70) dew = 1;
    if (spread <= 1 && (h.wind ?? 5) < 2 && h.total < 40) fog = 2;
    else if (spread <= 1.5 && calm && clearish) fog = 1;
  }
  if (h.visibilityKm != null) {
    if (h.visibilityKm < 1) fog = 2;
    else if (h.visibilityKm < 5) fog = Math.max(fog, 1) as Risk;
  }
  if (h.code === 45 || h.code === 48) fog = 2;
  return { dew, fog, frost: h.temp != null && h.temp <= 1 && clearish };
}

/**
 * The forecast hour by hour: the hours of the main forecast, and any others the ensembles cover. The chance of a
 * clear sky blends the two where there are both, and takes whichever there is otherwise.
 */
export function mergeHours(
  base: CloudHour[],
  ens: Map<number, number> | null,
  aod: Map<number, number | null> | null,
  see: Map<number, [number | null, number]> | null,
  now = Date.now(),
): SkyHour[] {
  const main = new Map(base.map((h) => [h.t, h]));
  const times = [...new Set([...main.keys(), ...(ens?.keys() ?? [])])].sort((a, b) => a - b);
  return times.map((t) => {
    const h = main.get(t);
    const e = ens?.get(t) ?? null;
    const det = h ? detClear(h.total) : null;
    const w = detWeight((t - now) / HOUR);
    const pClear = det == null ? e : e == null ? det : w * det + (1 - w) * e;
    const a = aod?.get(t) ?? null;
    const kV = a != null ? extinction(a, h?.pressure ?? 1013.25) : null;
    const s = see?.get(t);
    return {
      t,
      cloud: h?.total ?? null,
      high: h?.high ?? null,
      ens: e,
      pClear,
      seeing: s?.[0] ?? null,
      jet: s?.[1] ?? null,
      kV,
      transparency: kV != null ? transparencyGrade(kV, h?.high ?? null, h?.visibilityKm ?? null) : null,
      ...(h ? dewFog(h) : { dew: 0 as Risk, fog: 0 as Risk, frost: false }),
      temp: h?.temp ?? null,
      feels: h?.feels ?? null,
      wind: h?.wind ?? null,
    };
  });
}

/** The forecast last loaded. `pending` while only the main forecast is in and the other parts are still coming. */
export const skyHours = signal<{ key: string; hours: SkyHour[]; parts: { ensemble: boolean; aerosol: boolean; seeing: boolean }; pending?: boolean } | null>(null);

/**
 * Load every part of the forecast for a place; missing parts are simply left out. The main forecast is enough
 * to go on: if the other parts are slow to come (a poor connection), `early` has the hours from it meanwhile.
 */
export async function loadSkyForecast(p: Place, early?: (hours: SkyHour[]) => void): Promise<SkyHour[] | null> {
  const key = `${p.lat.toFixed(2)},${p.lon.toFixed(2)}`;
  const extras = Promise.all([loadEnsemble(p), loadAerosol(p), loadSeeing(p)]);
  const base = (await loadClouds(p)) ?? [];
  // A moment's grace first, so on a good connection the whole forecast comes at once.
  const soon = setTimeout(() => {
    const hours = mergeHours(base, null, null, null);
    if (!hours.length) return;
    skyHours.value = { key, hours, parts: { ensemble: false, aerosol: false, seeing: false }, pending: true };
    early?.(hours);
  }, 2000);
  const [ens, aod, see] = await extras;
  clearTimeout(soon);
  const hours = mergeHours(
    base,
    ens ? new Map(ens) : null,
    aod ? new Map(aod) : null,
    see ? new Map(see.map(([t, s, j]) => [t, [s, j] as [number | null, number]])) : null,
  );
  if (!hours.length) return null;
  skyHours.value = { key, hours, parts: { ensemble: !!ens, aerosol: !!aod, seeing: !!see } };
  return hours;
}

// ---------- A score out of ten ----------
// Hour by hour: 10 × chance of clear sky × how dark the sky gets compared with the best this place can
// offer (Moon, twilight and haze all cost limiting magnitude) × a fog penalty. The night's score is its
// best two hours, and the biggest cost is named so people know what is holding it back.
export type Limit = 'cloud' | 'moon' | 'twilight' | 'haze' | 'fog' | 'lights';
const LIMITS: Limit[] = ['cloud', 'moon', 'twilight', 'haze', 'fog', 'lights'];

export interface HourScore {
  t: number;
  score: number;
  sunAlt: number;
  /** The eye's limit overhead that hour, as Naked eye has it (with no nudge), less the haze. */
  limit: number;
  /** How much of the score (0–1) each thing takes away. */
  cost: Record<Limit, number>;
}

export interface NightScore {
  hours: HourScore[];
  /** The best two dark hours, and up to two things that hold them back, worst first. */
  best: { start: number; end: number; score: number; limits: Limit[] } | null;
}

/** Altitude in degrees, geometric (no refraction), as twilight is defined and as Naked eye has it. */
function altitude(body: A.Body, d: Date, obs: A.Observer) {
  const e = A.Equator(body, d, obs, true, true);
  return A.Horizon(d, obs, e.ra, e.dec).altitude;
}

/**
 * Stargazing rating for each forecast hour, and the best two dark hours of the night. Sky quality mixes
 * how dark the sky is for this place (60%) with how dark it is in absolute terms (40%: naked-eye limit 3
 * scores nothing, 6.5 full marks), so a clear night in a city rates well but never as well as a dark site.
 * The limit is Naked eye's own (render/nakedeye.ts, eyeLimit), for the Sun and Moon where Naked eye has them
 * (true altitudes), with no "I can't see it" nudge: that is for the sky on show now, not the hours ahead.
 */
export function scoreNight(hours: SkyHour[], obs: A.Observer, from: number, to: number, bortle: number, sqm: number | null, now = Date.now()): NightScore {
  const bestSky = eyeLimit(-30, -30, 0, bortle, sqm, 0);
  const quality = (nelm: number) => 0.6 * clamp(1 - (bestSky - nelm) / 2.5, 0, 1) + 0.4 * clamp((nelm - 3) / 3.5, 0, 1);
  const lights = 1 - quality(bestSky);
  const scored: HourScore[] = [];
  for (const h of hours) {
    if (h.t < from || h.t > to || h.pClear == null) continue;
    const d = new Date(h.t);
    const sunAlt = altitude(A.Body.Sun, d, obs);
    const moonAlt = altitude(A.Body.Moon, d, obs);
    const moon = A.Illumination(A.Body.Moon, d);
    const haze = h.kV != null ? Math.max(0, h.kV - 0.3) * 1.4 : 0; // extra dimming at about 45° altitude
    const nelm = eyeLimit(sunAlt, moonAlt, moon.mag, bortle, sqm, 0) - haze;
    const noMoon = eyeLimit(sunAlt, -30, 0, bortle, sqm, 0) - haze;
    const noTwilight = eyeLimit(-30, moonAlt, moon.mag, bortle, sqm, 0) - haze;
    const sky = quality(nelm);
    const fog = h.fog === 2 ? 0.3 : h.fog === 1 ? 0.75 : 1;
    scored.push({
      t: h.t,
      sunAlt,
      limit: nelm,
      score: 10 * h.pClear * sky * fog,
      cost: {
        cloud: 1 - h.pClear,
        // A bright Moon in the sky is always worth naming, even where city lights already hide most of
        // what it would.
        moon: Math.max(quality(noMoon) - sky, moonAlt > 5 && moon.phase_fraction > 0.5 ? 0.12 : 0),
        twilight: quality(noTwilight) - sky,
        haze: quality(nelm + haze) - sky,
        fog: 1 - fog,
        lights,
      },
    });
  }
  // Only properly dark hours count (the darkest kind of night this one offers for half an hour or more:
  // astronomical, else nautical, else civil), and none already past.
  // A white night with no such stretch is rated on its hours after sunset, which twilight holds back.
  let limit = 0;
  let dark: HourScore[] = [];
  for (const c of [-18, -12, -6, 0]) {
    dark = scored.filter((s) => s.sunAlt < c && s.t >= now - HOUR && (c === 0 || darkUntil(s.t, obs, c) - s.t >= HOUR / 2));
    if (dark.length) {
      limit = c;
      break;
    }
  }
  let best: NightScore['best'] = null;
  for (let i = 0; i < dark.length; i++) {
    const next = dark[i + 1] && dark[i + 1].t - dark[i].t <= HOUR ? dark[i + 1] : null;
    // The last hour of a dark spell is already covered by the pair before it.
    if (!next && i > 0 && dark[i].t - dark[i - 1].t <= HOUR) continue;
    const pair = next ? [dark[i], next] : [dark[i]];
    const score = pair.reduce((a, s) => a + s.score, 0) / pair.length;
    if (!best || score > best.score + 0.05) {
      const cost = (k: Limit) => pair.reduce((a, s) => a + s.cost[k], 0) / pair.length;
      const limits = LIMITS.map((k) => [k, cost(k)] as const)
        .filter(([, c]) => c >= 0.12)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 2)
        .map(([k]) => k);
      // City lights never change, but the Moon does: when it is bright and up, it is always named.
      if (cost('moon') >= 0.12 && !limits.includes('moon')) limits.splice(1, 1, 'moon');
      best = { start: pair[0].t, end: darkUntil(pair[pair.length - 1].t, obs, limit), score, limits };
    }
  }
  return { hours: scored, best };
}

/** The end of a dark hour starting at t: an hour later, or sooner if dawn comes first. */
function darkUntil(t: number, obs: A.Observer, limit: number) {
  if (altitude(A.Body.Sun, new Date(t + HOUR), obs) < limit) return t + HOUR;
  let lo = t, hi = t + HOUR;
  for (let i = 0; i < 12; i++) {
    const m = (lo + hi) / 2;
    if (altitude(A.Body.Sun, new Date(m), obs) < limit) lo = m;
    else hi = m;
  }
  return Math.floor(lo / 300e3) * 300e3; // to the five minutes before
}
